import React, {useEffect, useState, useCallback} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {
    Menu,
    X,
    Link2,
    Shield,
    Activity,
    Bot,
    LogOut,
    ChevronRight,
    Zap
} from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import './Navbar.css';

const WEB_SECRET_KEY = process.env.REACT_APP_WEB_SECRET_KEY || 'default_secret_key';

/**
 * Generates unique hash for user ID using Web Crypto API
 */
async function generateHash(number) {
    const keyBuffer = new TextEncoder().encode(WEB_SECRET_KEY);
    const numberBuffer = new TextEncoder().encode(number.toString());

    const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBuffer,
        {name: 'HMAC', hash: 'SHA-256'},
        false,
        ['sign']
    );

    const signature = await crypto.subtle.sign('HMAC', cryptoKey, numberBuffer);
    const hashArray = Array.from(new Uint8Array(signature));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Premium Navbar component with mobile responsiveness
 */
const Navbar = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [userId, setUserId] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [token, setToken] = useState('');
    const [scrolled, setScrolled] = useState(false);

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll, {passive: true});
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close menu on route change
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    // Close menu on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    // Prevent body scroll when menu is open
    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [menuOpen]);

    useEffect(() => {
        const storedUserId = localStorage.getItem('user_id');
        if (storedUserId) {
            setUserId(storedUserId);
            generateHash(parseInt(storedUserId)).then(hash => {
                setToken(hash);
            }).catch(err => {
                console.error('Error generating hash:', err);
            });
        }
    }, []);

    const handleLogout = useCallback(() => {
        if (!window.confirm(t('common.confirm'))) {
            return;
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user_id');
        localStorage.removeItem('tokenExpiry');
        navigate('/login');
    }, [navigate, t]);

    const toggleMenu = () => setMenuOpen(!menuOpen);
    const closeMenu = () => setMenuOpen(false);

    const isActive = (path) => location.pathname === path;

    const navItems = [
        {
            path: '/connections',
            label: t('nav.connections'),
            icon: Link2,
            internal: true
        },
        {
            path: '/hedge-configs',
            label: t('nav.hedgeConfigs'),
            icon: Shield,
            internal: true
        },
        {
            path: userId ? `https://hs.cdzv.com/hedger/${userId}?token=${token}` : null,
            label: 'Active Hedges',
            icon: Activity,
            external: true,
            show: !!userId
        },
        {
            path: 'https://t.me/c_hedge_bot',
            label: 'Telegram Bot',
            icon: Bot,
            external: true
        }
    ];

    return (
        <>
            <nav className={`navbar ${scrolled ? 'scrolled' : ''} ${menuOpen ? 'menu-open' : ''}`}>
                <div className="navbar-container">
                    {/* Brand Section */}
                    <div className="navbar-brand">
                        <Link to="/connections" className="brand-link" onClick={closeMenu}>
                            <div className="brand-icon">
                                <Zap size={24} />
                            </div>
                            <div className="brand-text">
                                <span className="brand-name">Hedger</span>
                                <span className="brand-id">#{userId}</span>
                            </div>
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="navbar-nav desktop-nav">
                        {navItems.map((item, index) => {
                            if (item.show === false) return null;
                            if (!item.path) return null;

                            if (item.internal) {
                                return (
                                    <Link
                                        key={index}
                                        to={item.path}
                                        className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                                    >
                                        <item.icon size={18} />
                                        <span>{item.label}</span>
                                    </Link>
                                );
                            }

                            return (
                                <a
                                    key={index}
                                    href={item.path}
                                    className="nav-link external"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <item.icon size={18} />
                                    <span>{item.label}</span>
                                </a>
                            );
                        })}
                    </div>

                    {/* Desktop Actions */}
                    <div className="navbar-actions desktop-actions">
                        <LanguageSwitcher />
                        <button onClick={handleLogout} className="logout-btn" title="Logout">
                            <LogOut size={20} />
                        </button>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <button
                        className={`menu-toggle ${menuOpen ? 'open' : ''}`}
                        onClick={toggleMenu}
                        aria-label="Toggle navigation menu"
                        aria-expanded={menuOpen}
                    >
                        {menuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            <div
                className={`mobile-overlay ${menuOpen ? 'open' : ''}`}
                onClick={closeMenu}
                aria-hidden="true"
            />

            {/* Mobile Menu Panel */}
            <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
                <div className="mobile-menu-header">
                    <div className="mobile-user-info">
                        <div className="mobile-user-avatar">
                            <Zap size={24} />
                        </div>
                        <div className="mobile-user-details">
                            <span className="mobile-user-name">Hedger Panel</span>
                            <span className="mobile-user-id">User #{userId}</span>
                        </div>
                    </div>
                </div>

                <nav className="mobile-nav">
                    {navItems.map((item, index) => {
                        if (item.show === false) return null;
                        if (!item.path) return null;

                        if (item.internal) {
                            return (
                                <Link
                                    key={index}
                                    to={item.path}
                                    className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                                    onClick={closeMenu}
                                >
                                    <div className="mobile-nav-icon">
                                        <item.icon size={22} />
                                    </div>
                                    <span className="mobile-nav-label">{item.label}</span>
                                    <ChevronRight size={18} className="mobile-nav-arrow" />
                                </Link>
                            );
                        }

                        return (
                            <a
                                key={index}
                                href={item.path}
                                className="mobile-nav-link external"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={closeMenu}
                            >
                                <div className="mobile-nav-icon">
                                    <item.icon size={22} />
                                </div>
                                <span className="mobile-nav-label">{item.label}</span>
                                <ChevronRight size={18} className="mobile-nav-arrow" />
                            </a>
                        );
                    })}
                </nav>

                <div className="mobile-menu-footer">
                    <div className="mobile-language">
                        <LanguageSwitcher />
                    </div>
                    <button onClick={handleLogout} className="mobile-logout-btn">
                        <LogOut size={20} />
                        <span>Logout</span>
                    </button>
                </div>
            </div>
        </>
    );
};

export default Navbar;
